import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  Snackbar,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';

interface Role {
  id: string;
  name: string;
  description: string;
  userCount: number;
  permissions: string[];
}

interface CreateRoleData {
  name: string;
  description: string;
  permissions: string[];
}

export const Roles: React.FC = () => {
  const { token, user } = useAuth();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [openPermissionDialog, setOpenPermissionDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState<CreateRoleData>({
    name: '',
    description: '',
    permissions: []
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRole, setMenuRole] = useState<Role | null>(null);
  
  // 通知状态
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const API_BASE_URL = 'https://d1-mysql-manager-api.5212025.xyz';

  // 检查管理员权限
  const isAdmin = user?.role.name === 'Administrator';

  // 所有可用权限
  const allPermissions = [
    { id: 'users.create', name: '创建用户', group: '用户管理' },
    { id: 'users.read', name: '查看用户', group: '用户管理' },
    { id: 'users.update', name: '更新用户', group: '用户管理' },
    { id: 'users.delete', name: '删除用户', group: '用户管理' },
    { id: 'roles.manage', name: '管理角色', group: '角色管理' },
    { id: 'roles.create', name: '创建角色', group: '角色管理' },
    { id: 'roles.update', name: '更新角色', group: '角色管理' },
    { id: 'roles.delete', name: '删除角色', group: '角色管理' },
    { id: 'connections.manage', name: '管理连接', group: '连接管理' },
    { id: 'connections.read', name: '查看连接', group: '连接管理' },
    { id: 'connections.create', name: '创建连接', group: '连接管理' },
    { id: 'connections.update', name: '更新连接', group: '连接管理' },
    { id: 'connections.delete', name: '删除连接', group: '连接管理' },
    { id: 'tables.read', name: '查看表数据', group: '数据操作' },
    { id: 'tables.insert', name: '插入数据', group: '数据操作' },
    { id: 'tables.update', name: '更新数据', group: '数据操作' },
    { id: 'tables.delete', name: '删除数据', group: '数据操作' },
    { id: 'audit.read', name: '查看审计日志', group: '审计管理' },
  ];

  // API调用辅助函数
  const apiCall = async (endpoint: string, options: RequestInit = {}) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: '请求失败' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  };

  // 验证和修复角色数据
  const validateAndFixRole = (role: any, index: number): Role => {
    console.log(`Processing role ${index}:`, role);
    
    // 检查是否ID被错误地设置为名称
    if (role.id === role.name) {
      console.warn(`Role ${index} has ID same as name, this is likely incorrect:`, role.id);
      
      // 基于角色名称分配正确的ID
      switch (role.name) {
        case 'Administrator':
          role.id = 'admin-role-001';
          break;
        case 'User':
          role.id = 'user-role-002';
          break;
        case 'ReadOnly':
          role.id = 'readonly-role-003';
          break;
        default:
          role.id = `role-${role.name.toLowerCase()}-${Date.now()}`;
      }
      console.log(`Fixed role ID for ${role.name}:`, role.id);
    }
    
    // 确保角色有有效的ID
    if (!role.id || typeof role.id !== 'string' || role.id.trim() === '') {
      console.warn(`Role ${index} has invalid ID:`, role.id, 'Using fallback ID based on name');
      // 创建基于名称的fallback ID
      if (role.name === 'Administrator') {
        role.id = 'admin-role-001';
      } else if (role.name === 'User') {
        role.id = 'user-role-002';
      } else if (role.name === 'ReadOnly') {
        role.id = 'readonly-role-003';
      } else {
        role.id = `role-${index}-${Date.now()}`;
      }
      console.log(`Assigned fallback ID:`, role.id);
    }
    
    // 确保必要字段存在
    return {
      id: role.id,
      name: role.name || 'Unknown Role',
      description: role.description || '',
      userCount: role.userCount || 0,
      permissions: Array.isArray(role.permissions) ? role.permissions : []
    };
  };

  // 加载角色列表
  const loadRoles = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/roles');
      console.log('Roles API response:', data); // Debug log
      console.log('Roles data:', data.data?.roles || []); // Debug log
      
      // 额外验证数据结构
      const rolesData = data.data?.roles || [];
      
      // 验证和修复角色数据
      const validatedRoles = rolesData.map((role: any, index: number) => {
        return validateAndFixRole(role, index);
      });
      
      console.log('Validated roles:', validatedRoles);
      setRoles(validatedRoles);
    } catch (error) {
      console.error('加载角色失败:', error);
      showSnackbar('加载角色列表失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 创建角色
  const createRole = async () => {
    try {
      const data = await apiCall('/api/roles', {
        method: 'POST',
        body: JSON.stringify(formData),
      });
      
      showSnackbar('角色创建成功', 'success');
      setOpenCreateDialog(false);
      resetForm();
      loadRoles();
    } catch (error) {
      console.error('创建角色失败:', error);
      showSnackbar((error as Error).message || '创建角色失败', 'error');
    }
  };

  // 更新角色
  const updateRole = async () => {
    if (!editingRole) {
      console.error('updateRole: editingRole is null or undefined');
      return;
    }

    try {
      console.log('=== updateRole Debug Info ===');
      console.log('editingRole object:', editingRole);
      console.log('editingRole.id:', editingRole.id);
      console.log('editingRole.name:', editingRole.name);
      console.log('typeof editingRole.id:', typeof editingRole.id);
      console.log('formData:', formData);
      
      // 验证角色ID
      if (!editingRole.id || typeof editingRole.id !== 'string') {
        console.error('Invalid role ID:', editingRole.id);
        showSnackbar('角色ID无效，无法更新', 'error');
        return;
      }
      
      const apiUrl = `/api/roles/${editingRole.id}`;
      console.log('API URL:', `${API_BASE_URL}${apiUrl}`);
      
      await apiCall(apiUrl, {
        method: 'PUT',
        body: JSON.stringify(formData),
      });

      showSnackbar('角色更新成功', 'success');
      setOpenCreateDialog(false);
      setEditingRole(null);
      resetForm();
      loadRoles();
    } catch (error) {
      console.error('更新角色失败:', error);
      showSnackbar((error as Error).message || '更新角色失败', 'error');
    }
  };

  // 删除角色
  const deleteRole = async (roleId: string) => {
    if (!window.confirm('确定要删除这个角色吗？此操作不可撤销。')) {
      return;
    }

    try {
      await apiCall(`/api/roles/${roleId}`, {
        method: 'DELETE',
      });

      showSnackbar('角色删除成功', 'success');
      loadRoles();
    } catch (error) {
      console.error('删除角色失败:', error);
      showSnackbar((error as Error).message || '删除角色失败', 'error');
    }
  };

  // 显示通知
  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  // 重置表单
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      permissions: []
    });
  };

  // 获取角色颜色
  const getRoleColor = (roleName: string) => {
    switch (roleName) {
      case 'Administrator': return 'error';
      case 'User': return 'primary';
      case 'ReadOnly': return 'secondary';
      default: return 'default';
    }
  };

  // 权限映射到中文
  const permissionMap: Record<string, string> = {
    'users.create': '创建用户',
    'users.read': '查看用户',
    'users.update': '更新用户',
    'users.delete': '删除用户',
    'roles.manage': '管理角色',
    'roles.create': '创建角色',
    'roles.update': '更新角色',
    'roles.delete': '删除角色',
    'connections.manage': '管理连接',
    'connections.read': '查看连接',
    'connections.create': '创建连接',
    'connections.update': '更新连接',
    'connections.delete': '删除连接',
    'tables.read': '查看表数据',
    'tables.insert': '插入数据',
    'tables.update': '更新数据',
    'tables.delete': '删除数据',
    'audit.read': '查看审计日志',
  };

  // 权限分组
  const groupPermissions = (permissions: string[]) => {
    const groups: Record<string, string[]> = {
      '用户管理': [],
      '角色管理': [],
      '连接管理': [],
      '数据操作': [],
      '审计管理': [],
    };

    permissions.forEach(perm => {
      if (perm.startsWith('users.')) {
        groups['用户管理'].push(perm);
      } else if (perm.startsWith('roles.')) {
        groups['角色管理'].push(perm);
      } else if (perm.startsWith('connections.')) {
        groups['连接管理'].push(perm);
      } else if (perm.startsWith('tables.')) {
        groups['数据操作'].push(perm);
      } else if (perm.startsWith('audit.')) {
        groups['审计管理'].push(perm);
      }
    });

    return groups;
  };

  // 按分组获取权限
  const getPermissionsByGroup = () => {
    return allPermissions.reduce((groups, permission) => {
      if (!groups[permission.group]) {
        groups[permission.group] = [];
      }
      groups[permission.group].push(permission);
      return groups;
    }, {} as Record<string, typeof allPermissions>);
  };

  // 查看权限详情
  const handleViewPermissions = (role: Role) => {
    setSelectedRole(role);
    setOpenPermissionDialog(true);
  };

  // 打开创建对话框
  const handleCreate = () => {
    if (!isAdmin) {
      showSnackbar('权限不足：只有管理员可以管理角色', 'error');
      return;
    }
    setEditingRole(null);
    resetForm();
    setOpenCreateDialog(true);
  };

  // 打开编辑对话框
  const handleEdit = (role: Role) => {
    if (!isAdmin) {
      showSnackbar('权限不足：只有管理员可以管理角色', 'error');
      return;
    }
    
    console.log('handleEdit - role object:', role); // Debug log
    console.log('handleEdit - role.id:', role.id); // Debug log
    console.log('handleEdit - role.name:', role.name); // Debug log
    
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description,
      permissions: [...role.permissions]
    });
    setOpenCreateDialog(true);
    setAnchorEl(null);
  };

  // 处理删除
  const handleDelete = (role: Role) => {
    if (!isAdmin) {
      showSnackbar('权限不足：只有管理员可以管理角色', 'error');
      return;
    }
    
    if (role.id === 'admin') {
      showSnackbar('不能删除管理员角色', 'error');
      return;
    }
    
    deleteRole(role.id);
    setAnchorEl(null);
  };

  // 处理表单提交
  const handleSubmit = () => {
    if (!formData.name || !formData.description) {
      showSnackbar('请填写角色名称和描述', 'error');
      return;
    }

    if (editingRole) {
      updateRole();
    } else {
      createRole();
    }
  };

  // 权限选择处理
  const handlePermissionChange = (permissionId: string, checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        permissions: [...prev.permissions, permissionId]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        permissions: prev.permissions.filter(p => p !== permissionId)
      }));
    }
  };

  // 菜单操作
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, role: Role) => {
    setAnchorEl(event.currentTarget);
    setMenuRole(role);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRole(null);
  };

  useEffect(() => {
    loadRoles();
  }, []);

  if (loading) {
    return <LoadingSpinner message="加载角色数据..." />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          角色管理
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          disabled={!isAdmin}
        >
          创建角色
        </Button>
      </Box>

      {!isAdmin && (
        <Alert severity="info" sx={{ mb: 3 }}>
          您当前的权限只能查看角色信息。如需管理角色，请联系管理员。
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3 }}>
        {roles.map((role) => (
          <Card key={role.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SecurityIcon sx={{ mr: 1, color: `${getRoleColor(role.name)}.main` }} />
                  <Typography variant="h6" component="div">
                    {role.name}
                  </Typography>
                </Box>
                {isAdmin && role.id !== 'admin' && (
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuClick(e, role)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                )}
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {role.description}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <GroupIcon sx={{ mr: 1, fontSize: 'small' }} />
                <Typography variant="body2">
                  {role.userCount} 个用户
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircleIcon sx={{ mr: 1, fontSize: 'small' }} />
                <Typography variant="body2">
                  {role.permissions.length} 个权限
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {role.permissions.slice(0, 3).map((permission) => (
                  <Chip
                    key={permission}
                    label={permissionMap[permission] || permission}
                    size="small"
                    variant="outlined"
                  />
                ))}
                {role.permissions.length > 3 && (
                  <Chip
                    label={`+${role.permissions.length - 3}`}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                )}
              </Box>
            </CardContent>
            
            <CardActions>
              <Button
                size="small"
                startIcon={<InfoIcon />}
                onClick={() => handleViewPermissions(role)}
              >
                查看权限
              </Button>
              {isAdmin && role.id !== 'admin' && (
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleEdit(role)}
                >
                  编辑
                </Button>
              )}
            </CardActions>
          </Card>
        ))}
      </Box>

      {/* 操作菜单 */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEdit(menuRole!)}>
          <EditIcon sx={{ mr: 1 }} />
          编辑
        </MenuItem>
        <MenuItem onClick={() => handleDelete(menuRole!)}>
          <DeleteIcon sx={{ mr: 1 }} />
          删除
        </MenuItem>
      </Menu>

      {/* 权限详情对话框 */}
      <Dialog 
        open={openPermissionDialog} 
        onClose={() => setOpenPermissionDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedRole?.name} - 权限详情
        </DialogTitle>
        <DialogContent>
          {selectedRole && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {selectedRole.description}
              </Typography>

              {Object.entries(groupPermissions(selectedRole.permissions)).map(([group, perms]) => (
                perms.length > 0 && (
                  <Box key={group} sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {group}
                    </Typography>
                    <List dense>
                      {perms.map((permission) => (
                        <ListItem key={permission}>
                          <ListItemIcon>
                            <CheckCircleIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={permissionMap[permission] || permission}
                            secondary={permission}
                          />
                        </ListItem>
                      ))}
                    </List>
                    <Divider />
                  </Box>
                )
              ))}

              <Alert severity="info" sx={{ mt: 2 }}>
                该角色当前被 {selectedRole.userCount} 个用户使用
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPermissionDialog(false)}>
            关闭
          </Button>
        </DialogActions>
      </Dialog>

      {/* 创建/编辑角色对话框 */}
      <Dialog 
        open={openCreateDialog} 
        onClose={() => setOpenCreateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingRole ? '编辑角色' : '创建角色'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="角色名称"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            
            <TextField
              label="角色描述"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              required
            />

            <Typography variant="h6" sx={{ mt: 2 }}>
              权限配置
            </Typography>

            {Object.entries(getPermissionsByGroup()).map(([group, permissions]) => (
              <Accordion key={group}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{group}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormGroup>
                    {permissions.map((permission) => (
                      <FormControlLabel
                        key={permission.id}
                        control={
                          <Checkbox
                            checked={formData.permissions.includes(permission.id)}
                            onChange={(e) => handlePermissionChange(permission.id, e.target.checked)}
                          />
                        }
                        label={permission.name}
                      />
                    ))}
                  </FormGroup>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>
            取消
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingRole ? '更新' : '创建'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 通知 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}; 